import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { fuseAnimations } from "@fuse/animations";
import moment from "moment";
import { Subscription } from "rxjs";
import { DashboardPermissionItem } from "../../../../../../../shared/cw-backend/models/security/restrictable-component-permission";
import { DashboardProvider } from "./../dashboard.provider";

interface DynamicDashboardFiltersChange {
    filters: Record<string, any>;
    reportCodes?: string[];
}

interface DynamicDashboardChartTypeChange {
    reportCode: string;
    chartType: string;
}

@Component({
    selector: "app-dashboard-schedule",
    templateUrl: "./dashboard-schedule.component.html",
    styleUrls: ["./dashboard-schedule.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class DashboardScheduleComponent implements OnChanges, OnDestroy {
    @Input() company: any;
    @Input() filterData: any;
    @Input() permissions: DashboardPermissionItem[] = [];
    @Input() expandCommand: { expanded: boolean; version: number } | null = null;
    @Input() filtersEnabled = true;

    dynamicReports: any[] = [];
    loadingDynamicReports = false;
    loadingReportCodes: string[] = [];
    loadedReportCodes: string[] = [];
    reportFilters: Record<string, any> = {};
    reportChartTypes: Record<string, string> = {};

    private loadVersion = 0;
    private activeReportLoadCount = 0;
    private readonly maxConcurrentReportLoads = 2;
    private readonly pendingReportCodes = new Set<string>();
    private readonly activeReportCodes = new Set<string>();
    private readonly loadedReportCodeSet = new Set<string>();
    private readonly reportRequestVersionByCode: Record<string, number> = {};
    private metadataSubscription?: Subscription;
    private readonly reportLoadSubscriptions = new Map<string, Subscription>();

    constructor(private dashboardProvider: DashboardProvider) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["permissions"] || changes["filterData"]) {
            void this.loadDynamicReportMetadata();
        }
    }

    ngOnDestroy(): void {
        this.loadVersion += 1;
        this.cancelDashboardRequests();
    }

    private loadDynamicReportMetadata(): void {
        const params = this.getDashboardParams();

        if (!params) {
            this.dynamicReports = [];
            this.clearReportLoadState();
            return;
        }

        const currentVersion = ++this.loadVersion;
        this.clearReportLoadState();
        this.dynamicReports = [];
        this.loadingDynamicReports = true;

        this.metadataSubscription = this.dashboardProvider
            .getDynamicDashboardReportsObservable(
                "DS",
                params.dateStart,
                params.dateEnd,
                params.performerId,
                params.companyId,
                params.dashboardUserId,
                params.dashboardMonth,
                params.dashboardYear,
                "application-dashboards-admin",
                this.reportFilters,
                undefined,
                this.reportChartTypes,
                false,
            )
            .subscribe({
                next: (data) => {
                    if (currentVersion === this.loadVersion) {
                        this.dynamicReports = data.body || [];
                    }
                },
                error: () => {
                    if (currentVersion === this.loadVersion) {
                        this.loadingDynamicReports = false;
                    }
                },
                complete: () => {
                    if (currentVersion === this.loadVersion) {
                        this.loadingDynamicReports = false;
                    }
                },
            });
    }

    onReportsVisible(reportCodes: string[]): void {
        this.queueReportLoads(reportCodes);
    }

    private queueReportLoads(reportCodes: string[], forceReload = false): void {
        const availableReports = new Map(
            (this.dynamicReports || []).map((report) => [report.code, report]),
        );

        (reportCodes || []).forEach((code) => {
            const report = availableReports.get(code);

            if (!report || report.restrictionType === "D") {
                return;
            }

            if (forceReload) {
                this.loadedReportCodeSet.delete(code);
                this.reportRequestVersionByCode[code] =
                    (this.reportRequestVersionByCode[code] || 0) + 1;
                this.cancelReportLoad(code);
                this.syncLoadedReportCodes();
            }

            if (
                !forceReload &&
                (this.loadedReportCodeSet.has(code) ||
                    this.activeReportCodes.has(code) ||
                    this.pendingReportCodes.has(code))
            ) {
                return;
            }

            this.pendingReportCodes.add(code);
        });

        this.syncLoadingReportCodes();
        this.drainReportLoadQueue();
    }

    private drainReportLoadQueue(): void {
        while (
            this.activeReportLoadCount < this.maxConcurrentReportLoads &&
            this.pendingReportCodes.size
        ) {
            const code = Array.from(this.pendingReportCodes).find(
                (pendingCode) => !this.activeReportCodes.has(pendingCode),
            );

            if (!code) {
                break;
            }

            this.pendingReportCodes.delete(code);
            this.loadSingleDynamicReport(code);
        }

        this.syncLoadingReportCodes();
    }

    private loadSingleDynamicReport(reportCode: string): void {
        const params = this.getDashboardParams();

        if (!params) {
            return;
        }

        const currentLoadVersion = this.loadVersion;
        const currentReportVersion =
            this.reportRequestVersionByCode[reportCode] || 0;

        this.activeReportLoadCount += 1;
        this.activeReportCodes.add(reportCode);
        this.syncLoadingReportCodes();

        const subscription = this.dashboardProvider
            .getDynamicDashboardReportsObservable(
                "DS",
                params.dateStart,
                params.dateEnd,
                params.performerId,
                params.companyId,
                params.dashboardUserId,
                params.dashboardMonth,
                params.dashboardYear,
                "application-dashboards-admin",
                this.reportFilters,
                [reportCode],
                this.reportChartTypes,
                true,
            )
            .subscribe({
                next: (data) => {
                    if (
                        currentLoadVersion === this.loadVersion &&
                        currentReportVersion ===
                            (this.reportRequestVersionByCode[reportCode] || 0)
                    ) {
                        const reports = data.body || [];

                        this.dynamicReports = this.mergeDynamicReports(reports, [
                            reportCode,
                        ]);
                        this.loadedReportCodeSet.add(reportCode);
                        this.syncLoadedReportCodes();
                    }
                },
                error: () => this.finishReportLoad(reportCode, currentLoadVersion),
                complete: () => this.finishReportLoad(reportCode, currentLoadVersion),
            });

        this.reportLoadSubscriptions.set(reportCode, subscription);
    }

    onReportFiltersChange(
        payload: Record<string, any> | DynamicDashboardFiltersChange,
    ): void {
        const nextReportFilters = this.getReportFiltersPayload(payload);
        const changedReportCodes = this.getChangedReportCodesPayload(
            payload,
            nextReportFilters,
        );

        if (!changedReportCodes.length) {
            return;
        }

        this.reportFilters = nextReportFilters;
        this.queueReportLoads(changedReportCodes, true);
    }

    onReportChartTypeChange(change: DynamicDashboardChartTypeChange): void {
        if (!change?.reportCode || !change.chartType) {
            return;
        }

        this.reportChartTypes = {
            ...this.reportChartTypes,
            [change.reportCode]: change.chartType,
        };

        this.queueReportLoads([change.reportCode], true);
    }

    private getReportFiltersPayload(
        payload: Record<string, any> | DynamicDashboardFiltersChange,
    ): Record<string, any> {
        if (payload && "filters" in payload) {
            return payload.filters || {};
        }

        return payload || {};
    }

    private getChangedReportCodesPayload(
        payload: Record<string, any> | DynamicDashboardFiltersChange,
        reportFilters: Record<string, any>,
    ): string[] {
        if (payload && "reportCodes" in payload && payload.reportCodes?.length) {
            return payload.reportCodes;
        }

        return this.getChangedReportCodes(reportFilters);
    }

    private getChangedReportCodes(reportFilters: Record<string, any>): string[] {
        const changedReportCodes = new Set<string>();
        const allReportCodes = new Set<string>([
            ...Object.keys(this.reportFilters || {}),
            ...Object.keys(reportFilters || {}),
        ]);

        allReportCodes.forEach((code) => {
            if (
                JSON.stringify(this.reportFilters?.[code] || {}) !==
                JSON.stringify(reportFilters?.[code] || {})
            ) {
                changedReportCodes.add(code);
            }
        });

        return Array.from(changedReportCodes);
    }

    private mergeDynamicReports(
        nextReports: any[],
        reportCodesToReplace: string[],
    ): any[] {
        const nextReportsByCode = new Map(
            (nextReports || []).map((report) => [report.code, report]),
        );
        const reportCodes = new Set(reportCodesToReplace);

        return (this.dynamicReports || []).map((report) =>
            reportCodes.has(report.code) && nextReportsByCode.has(report.code)
                ? nextReportsByCode.get(report.code)
                : report,
        );
    }

    private clearReportLoadState(): void {
        this.cancelDashboardRequests();
        this.pendingReportCodes.clear();
        this.activeReportCodes.clear();
        this.loadedReportCodeSet.clear();
        this.activeReportLoadCount = 0;
        Object.keys(this.reportRequestVersionByCode).forEach(
            (code) => delete this.reportRequestVersionByCode[code],
        );
        this.syncLoadingReportCodes();
        this.syncLoadedReportCodes();
    }

    private cancelDashboardRequests(): void {
        this.metadataSubscription?.unsubscribe();
        this.metadataSubscription = undefined;

        this.reportLoadSubscriptions.forEach((subscription) =>
            subscription.unsubscribe(),
        );
        this.reportLoadSubscriptions.clear();
        this.pendingReportCodes.clear();
        this.activeReportCodes.clear();
        this.activeReportLoadCount = 0;
        this.syncLoadingReportCodes();
    }

    private cancelReportLoad(reportCode: string): void {
        const subscription = this.reportLoadSubscriptions.get(reportCode);

        if (!subscription) {
            return;
        }

        subscription.unsubscribe();
        this.reportLoadSubscriptions.delete(reportCode);
        this.activeReportCodes.delete(reportCode);
        this.activeReportLoadCount = Math.max(this.activeReportLoadCount - 1, 0);
        this.syncLoadingReportCodes();
    }

    private finishReportLoad(reportCode: string, loadVersion: number): void {
        if (loadVersion !== this.loadVersion) {
            return;
        }

        this.reportLoadSubscriptions.delete(reportCode);
        this.activeReportLoadCount = Math.max(this.activeReportLoadCount - 1, 0);
        this.activeReportCodes.delete(reportCode);
        this.syncLoadingReportCodes();
        this.drainReportLoadQueue();
    }

    private syncLoadingReportCodes(): void {
        this.loadingReportCodes = Array.from(
            new Set([
                ...Array.from(this.pendingReportCodes),
                ...Array.from(this.activeReportCodes),
            ]),
        );
    }

    private syncLoadedReportCodes(): void {
        this.loadedReportCodes = Array.from(this.loadedReportCodeSet);
    }

    private getDashboardParams(): {
        dateStart: string;
        dateEnd: string;
        performerId: number | null;
        companyId: number | null;
        dashboardUserId: number | null;
        dashboardMonth: number | null;
        dashboardYear: number | null;
    } | null {
        const startDate = this.filterData?.startDate
            ? moment(this.filterData.startDate)
            : moment().startOf("isoWeek");
        const endDate = this.filterData?.endDate
            ? moment(this.filterData.endDate)
            : moment().endOf("isoWeek");

        return {
            performerId: this.filterData.performer
                ? this.filterData.performer.id
                : null,
            companyId: this.filterData.company ? this.filterData.company.id : null,
            dashboardUserId: this.filterData?.user
                ? this.filterData.user.id || this.filterData.user.userId
                : null,
            dashboardMonth: this.filterData?.dashboardMonth || null,
            dashboardYear: this.filterData?.dashboardYear || null,
            dateStart: startDate.format("YYYY-MM-DD").toString(),
            dateEnd: endDate.format("YYYY-MM-DD").toString(),
        };
    }
}
